import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Door } from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import {
  ButtonWithSpinner,
  CopyToClipBoard,
  InfoCardText,
  InputWithLabel,
  Link,
  Loading,
  TermsAndConditionsCheckbox,
  Text,
  useFetch,
} from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import { getSelectedStore } from '@/common/helpers/helpers'
import commonServices from '@/common/services/services'
import generalServices from '@/general/services/services'
import routes from '@/Router/routes.constants'

import { getQueryParams } from './helpers'
import * as Styles from './styles'

const SignupPage = () => {
  const theme = useTheme()

  const { t, i18n } = useTranslation()
  const [storeName, setStoreName] = useState('')
  const [termsAndConditions, setTermsAndConditions] = useState(false)
  const [error, setError] = useState('')
  const queryParams = getQueryParams(window.location)
  const navigate = useNavigate()
  const { fetchData: signUp } = useFetch(generalServices.signup, { doInitialRequest: false })
  const { info, updateContext } = useContext(AppContext)
  const {
    loading: gettingSocialToken,
    data: socialTokenData,
    error: errorSocialToken,
  } = useFetch(() => generalServices.getSocialToken(queryParams), { doInitialRequest: true })
  const {
    loading: gettingMerchantInfo,
    data,
    fetchData: getMerchantInfo,
  } = useFetch(commonServices.getShopInfo, { doInitialRequest: false })

  const { loading: gettingUserInfo, fetchData: getUserInfo } = useFetch(commonServices.getUserProfile, {
    doInitialRequest: false,
  })

  useEffect(() => {
    if (socialTokenData) {
      localStorage.jwt = socialTokenData.access
      localStorage.refresh = socialTokenData.refresh

      getMerchantInfo().then(merchantInfo => {
        getUserInfo().then(userInfo => {
          const selectedStore = getSelectedStore(merchantInfo.stores, userInfo?.default_store_id)
          localStorage.selectedStore = JSON.stringify(selectedStore)
          updateContext(info => ({
            ...info,
            ...merchantInfo,
            selectedStore,
            userInfo,
          }))

          if (!merchantInfo.needs_to_signup) {
            navigate(routes.dashboardPage)
          } else {
            setStoreName(merchantInfo.name)
          }
        })
      })
    }

    if (errorSocialToken) {
      navigate(routes.loginPage)
    }
  }, [errorSocialToken, getMerchantInfo, navigate, socialTokenData, updateContext, getUserInfo])

  const onSubmit = e => {
    e.preventDefault()
  }

  const handleChange = (e, change) => {
    change(e.target.value)
  }

  const formIsValid = () => {
    return storeName && termsAndConditions
  }

  const onSignUp = async () => {
    try {
      await signUp({ storeName, termsAndConditions })

      const storeInfo = await getMerchantInfo()
      updateContext(context => ({
        ...context,
        ...storeInfo,
      }))

      navigate(routes.dashboardPage)
    } catch (error) {
      setError(error.errorMessage)
    }
  }

  return (
    <Styles.Layout>
      <Styles.Container>
        <Styles.Form>
          <Loading show={gettingMerchantInfo || gettingSocialToken || gettingUserInfo}>
            <Styles.ImageContainer>
              <Styles.ReveniLogo src="/logos/logo_reveni.svg" alt="Reveni logo" />
            </Styles.ImageContainer>
            <InfoCardText lightText={t('empresas')} title={t('Registra tu tienda')} />
            <Text
              type="bodyMRegular"
              color={theme.colors.text.default}
              customStyles={{
                marginTop: theme.spacing.sp8,
                marginBottom: theme.spacing.sp32,
              }}
            >
              {t(
                'Registra tu tienda de Shopify y comienza a ofrecer una mejor experiencia de cambios y devoluciones a tus clientes.'
              )}
            </Text>
            <Styles.FormContainer onSubmit={onSubmit}>
              <InputWithLabel
                onChange={e => handleChange(e, setStoreName)}
                label={t('Escribe el nombre de tu tienda')}
                value={storeName}
                placeholder="Acme store"
                fullWidth
              />
              <Text
                type="bodyMRegular"
                customStyles={{
                  marginBottom: theme.spacing.sp16,
                }}
              >
                {t('La URL de tu portal de cambios y devoluciones será:')}
              </Text>
              <CopyToClipBoard Icon={Door} fullWidth value={data?.stores[0]?.returns_portal_url} />

              <Styles.ConditionsContainer>
                <TermsAndConditionsCheckbox
                  checked={termsAndConditions}
                  onChange={e => setTermsAndConditions(e.target.checked)}
                  language={i18n.language}
                  isUKMerchant={info?.address?.country?.code === 'GB'}
                  origin="merchants"
                />
              </Styles.ConditionsContainer>
            </Styles.FormContainer>
            {error && (
              <Text
                type="bodySRegular"
                color={theme.colors.danger.onSurface}
                customStyles={{
                  marginBottom: theme.spacing.sp16,
                }}
              >
                {error}
              </Text>
            )}
            <ButtonWithSpinner
              defaultLabel={t('Continuar')}
              loadingLabel={t('Creando cuenta')}
              disabled={!formIsValid()}
              fullWidth
              onClick={onSignUp}
            />
            <Text
              type="bodyMRegular"
              customStyles={{
                textAlign: 'center',
                marginTop: theme.spacing.sp8,
              }}
            >
              {t('¿Ya tienes cuenta?')} <Link href="/login">{t('Inicia sesión')}</Link>
            </Text>
          </Loading>
        </Styles.Form>
        <Styles.SideImageContainer>
          <Text type="titleL" color={theme.colors.text.inverse}>
            &quot;
            {t('Gracias a Reveni nuestros clientes hacen sus devoluciones de una manera mucho más sencilla y rápida.')}
            &quot;
          </Text>
          <Text
            type="titleM"
            color={theme.colors.text.inverse}
            customStyles={{
              marginTop: theme.spacing.sp24,
            }}
          >
            Nico Soto - Edmmond Studios
          </Text>
        </Styles.SideImageContainer>
      </Styles.Container>
    </Styles.Layout>
  )
}

export default SignupPage
